import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/hooks/useData';
import { useAuth } from '@/contexts/AuthContext';
import { resolveCardTheme } from '@/lib/cardThemes';
import './MobileAccountSwitcher.css';

const SWAP_MS = 720;

function initials(name) {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

export default function MobileAccountSwitcher() {
  const { workspace, list, switchWorkspace } = useWorkspace();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [swappingId, setSwappingId] = useState(null);
  const swapTimerRef = useRef(null);

  // Clear pending swap timer on unmount
  useEffect(() => () => {
    if (swapTimerRef.current) clearTimeout(swapTimerRef.current);
  }, []);

  // body class for cross-component coordination (e.g. hide QuickCaptureFab on mobile)
  useEffect(() => {
    document.body.classList.add('mas-active');
    return () => document.body.classList.remove('mas-active');
  }, []);

  // close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Order: active first, then others (most recently created first via WorkspaceContext)
  const orderedAccounts = useMemo(() => {
    if (!workspace) return list;
    return [workspace, ...list.filter((w) => w.id !== workspace.id)];
  }, [list, workspace]);

  const handleSelect = useCallback((id) => {
    if (!open) {
      setOpen(true);
      return;
    }
    if (swappingId) return; // ignore clicks during swap animation
    if (id === workspace?.id) {
      setOpen(false);
      return;
    }
    // Swap animation 720ms — karty CELE viditelne, keyframes konca v open pos.
    // Wallet ostane open po swape (no auto-close) — user vidi novu active kartu,
    // backdrop click zatvori wallet smoothne cez existujuci transition. Ziadny
    // post-swap "poskocia dole" jump.
    setSwappingId(id);
    swapTimerRef.current = setTimeout(() => {
      switchWorkspace(id);
      setSwappingId(null);
      swapTimerRef.current = null;
    }, SWAP_MS);
  }, [open, workspace, switchWorkspace, swappingId]);

  const goSettings = useCallback((e) => {
    e.stopPropagation();
    setOpen(false);
    navigate('/nastavenia');
  }, [navigate]);

  const handleSignOut = useCallback((e) => {
    e.stopPropagation();
    setOpen(false);
    signOut();
  }, [signOut]);

  // Theme toggle — persist v localStorage, aplikovať na <html data-theme="dark">
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    try { return localStorage.getItem('todue.theme') || 'light'; } catch { return 'light'; }
  });
  useEffect(() => {
    if (theme === 'dark') document.documentElement.setAttribute('data-theme', 'dark');
    else document.documentElement.removeAttribute('data-theme');
    try { localStorage.setItem('todue.theme', theme); } catch { /* ignore */ }
  }, [theme]);
  const toggleTheme = useCallback((e) => {
    e.stopPropagation();
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  if (!workspace || list.length === 0) return null;

  return (
    <div className={`mas-root ${open ? 'mas-open' : ''}`}>
      {/* Backdrop ako sibling .mas-cards aby jeho z-index 860 platil na root urovni
          (nie nested v mas-cards stacking contexte). mt-bar (880) a stripes (879)
          tym ostanu NAD blur overlay. */}
      <div className="mas-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />
      <div className={`mas-cards ${open ? 'mas-open' : ''}`}>

        {orderedAccounts.map((ws, i) => {
          const isActive = ws.id === workspace.id;
          // Stable color — derived from ws.id position in original `list` (not orderedAccounts).
          // Inak by sa pri prepnutí workspace zamenili farby (active first reordering).
          const stableIndex = list.findIndex((w) => w.id === ws.id);
          const theme = resolveCardTheme(ws?.settings?.card_color, stableIndex);
          const onKey = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSelect(ws.id);
            }
          };
          const isSwapIn = swappingId === ws.id;
          const isSwapOut = swappingId !== null && isActive && !isSwapIn;
          return (
            <div
              key={ws.id}
              role="button"
              tabIndex={0}
              className={`mas-card ${isActive ? 'is-active' : ''} ${isSwapIn ? 'is-swap-in' : ''} ${isSwapOut ? 'is-swap-out' : ''}`}
              style={{
                '--i': i,
                '--mas-c1': theme.c1,
                '--mas-c2': theme.c2,
              }}
              onClick={() => handleSelect(ws.id)}
              onKeyDown={onKey}
              aria-label={`Prepnúť na ${ws.name}`}
            >
              <span className="mas-grab" aria-hidden="true" />
              <div className="mas-body">
                <div className="mas-row">
                  <div className="mas-av">{initials(ws.name)}</div>
                  <div className="mas-info">
                    <div className="mas-name">{ws.name}</div>
                    <div className="mas-sub">
                      <b>{ws.type === 'sro' ? 's.r.o.' : 'SZČO'}</b>
                      {ws.ico ? <> · IČO {ws.ico}</> : null}
                    </div>
                  </div>
                  {isActive && (
                    <button
                      type="button"
                      className="mas-ibtn mas-theme"
                      onClick={toggleTheme}
                      aria-label={theme === 'dark' ? 'Svetlý režim' : 'Tmavý režim'}
                    >
                      {theme === 'dark' ? (
                        <svg viewBox="0 0 24 24">
                          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="4.2" />
                          <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.2 5.2l1.6 1.6M17.2 17.2l1.6 1.6M18.8 5.2l-1.6 1.6M6.8 17.2l-1.6 1.6" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
                <div className="mas-actions">
                  {isActive && (
                    <>
                      <button type="button" className="mas-ibtn" onClick={goSettings} aria-label="Nastavenia">
                        <svg viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="3" />
                          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h0a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v0a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
                        </svg>
                      </button>
                      <button type="button" className="mas-ibtn" onClick={handleSignOut} aria-label="Odhlásiť sa">
                        <svg viewBox="0 0 24 24">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <polyline points="16,17 21,12 16,7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}

      </div>
    </div>
  );
}
